import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { generateTempId } from '../lib/offline/offlineDb';
import { useOfflineMutation } from './useOfflineMutation';

export interface CompareItem {
  id: number | string;
  product_id: number;
  created_at?: string;
}

const COMPARE_QUERY_KEY = ['compare-items'] as const;

export function useCompareItems(enabled = true) {
  return useQuery<CompareItem[]>({
    queryKey: COMPARE_QUERY_KEY,
    queryFn: () => api<CompareItem[]>('/compare-items'),
    enabled,
    // Only poll while online — see VIVATISPDB-305.
    refetchInterval: () => (typeof navigator !== 'undefined' && navigator.onLine ? 5 * 60 * 1000 : false),
  });
}

export function useToggleCompareItem() {
  const queryClient = useQueryClient();

  return useOfflineMutation<number>({
    operationType: 'COMPARE_TOGGLE',
    feature: 'compare',
    mutationFn: (productId) =>
      api('/compare-items/toggle', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
      }),
    toPayload: (productId) => ({ productId }),
    invalidateKeys: [[...COMPARE_QUERY_KEY]],
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: COMPARE_QUERY_KEY });
      const previous = queryClient.getQueryData<CompareItem[]>(COMPARE_QUERY_KEY);
      const old = previous ?? [];

      const exists = old.some((item) => item.product_id === productId);
      const updated = exists
        ? old.filter((item) => item.product_id !== productId)
        : [
            ...old,
            {
              id: generateTempId(),
              product_id: productId,
              created_at: new Date().toISOString(),
            },
          ];

      queryClient.setQueryData(COMPARE_QUERY_KEY, updated);
      return { previous };
    },
    onError: (_err, _productId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(COMPARE_QUERY_KEY, context.previous);
      }
    },
  });
}

export function useClearCompareItems() {
  const queryClient = useQueryClient();

  return useOfflineMutation<void>({
    operationType: 'COMPARE_CLEAR',
    feature: 'compare',
    mutationFn: async () => {
      await api('/compare-items', { method: 'DELETE' });
    },
    toPayload: () => ({}),
    invalidateKeys: [[...COMPARE_QUERY_KEY]],
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: COMPARE_QUERY_KEY });
      const previous = queryClient.getQueryData<CompareItem[]>(COMPARE_QUERY_KEY);
      queryClient.setQueryData(COMPARE_QUERY_KEY, []);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(COMPARE_QUERY_KEY, context.previous);
      }
    },
  });
}
